import { Button, Icon, cn } from "@nexus/ui";
import type { ComponentProps, MouseEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/** Star toggle that pops when an item is starred and fades its color when unstarred. */
export function FavoriteToggleButton({
  isFavorite,
  onToggle,
  variant = "ghost",
  size = "icon",
  className,
}: {
  isFavorite: boolean;
  /** Omit when the viewer cannot change favorites; the star stays visible but disabled. */
  onToggle?: (event: MouseEvent<HTMLButtonElement>) => void;
  variant?: ComponentProps<typeof Button>["variant"];
  size?: ComponentProps<typeof Button>["size"];
  className?: string;
}) {
  const { t } = useTranslation();
  // Bumped on each star so the pop replays; a remounted button resets to 0 and never replays it.
  const [popKey, setPopKey] = useState(0);
  const isPopping = popKey > 0 && isFavorite;

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={cn("group/fav relative shrink-0", className)}
      aria-pressed={isFavorite}
      aria-label={
        isFavorite
          ? t("personaOpportunities.scripts.unfavoriteAria", "Remove from favorites")
          : t("personaOpportunities.scripts.favoriteAria", "Add to favorites")
      }
      disabled={!onToggle}
      onClick={(event) => {
        if (!isFavorite) setPopKey((current) => current + 1);
        onToggle?.(event);
      }}
    >
      {isPopping ? (
        <span
          key={`halo-${popKey}`}
          aria-hidden
          className="favorite-star-halo pointer-events-none absolute inset-1 rounded-full bg-warning/40"
        />
      ) : null}
      <Icon
        key={popKey}
        name="star"
        size="sm"
        color="inherit"
        className={cn(
          "relative transition-[color,fill,transform] duration-150 ease-out group-hover/fav:scale-110 group-active/fav:scale-90 motion-reduce:transform-none motion-reduce:transition-none",
          isFavorite
            ? "fill-warning text-warning"
            : "text-muted-foreground group-hover/fav:text-warning",
          isPopping && "favorite-star-pop",
        )}
        aria-hidden
      />
    </Button>
  );
}
